import Alert from '@risk-smart/themed-cloudscape-components/alert';
import HelpPanel from '@risk-smart/themed-cloudscape-components/help-panel';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { ApprovalStatus } from '@risksmart-app/domain/src/types/consts/approval-status';
import { ApprovalStatus as Approval_Status_Enum } from '@risksmart-app/domain/src/types/consts/approval-status';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import Loading from 'src/components/loading';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { CHANGE_REQUEST_OVERRIDE_ACTION } from '@/components/change-request-override-modal/changeRequestTypes';
import { useUpsertApproverResponses } from '@/hooks/mutations/change-request';
import { useGetUsers } from '@/hooks/queries';
import { useCanOverrideChangeRequest } from '@/hooks/queries/change-request/useCanOverrideChangeRequest';
import { useGetApprovalLevels } from '@/hooks/queries/change-request/useGetApprovalLevels';
import {
  type ChangeRequestPresentation,
  useGetChangeRequestsByParentSubscription,
} from '@/hooks/queries/change-request/useGetChangeRequestsByParentSubscription';
import { useChangeRequestResolution } from '@/hooks/useChangeRequestResolution';
import {
  getDisplayLevels,
  getOwners,
  useChangeRequests,
} from '@/hooks/useChangeRequests';
import { useClearParentFromCache } from '@/hooks/useClearParentFromCache';
import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { toLocalDate, toLocalDateTime } from '@/utils/dateUtils';
import { getFriendlyId } from '@/utils/friendlyId';

import { useGetDetailParentPath } from '../../routes/useGetDetailParentPath';
import ButtonDropdown from '../button-dropdown';
import ChangeRequestOverrideModal from '../change-request-override-modal/ChangeRequestOverrideModal';
import Select from '../form/select';
import { UserPreview } from './UserPreview';

type ChangeRequestLevelsProps = {
  changeRequestId?: string;
  parentId: string;
};

export const ChangeRequestLevels = ({
  changeRequestId,
  parentId,
}: ChangeRequestLevelsProps) => {
  const navigate = useNavigate();
  // RSP-6087/6099: the CR feed comes from tRPC (polled) when the `trpc` flag is
  // on — the Hasura subscription does not deliver in the V3 stack, which left
  // this panel spinning forever. The holding-pattern subscription hook gates
  // poll-vs-subscription internally (and is the single place the real tRPC SSE
  // subscription will slot into — RSP-4320).
  const { data: changeRequestsData, loading: crListLoading } =
    useGetChangeRequestsByParentSubscription({ queryArgs: { parentId } });
  const changeRequests = changeRequestsData?.change_request;
  const [_searchParams, setSearchParams] = useSearchParams();

  const { user, isLoading } = useRisksmartUser();

  const { t } = useTranslation('common', { keyPrefix: 'approvals' });
  const statuses = t('status', { returnObjects: true });

  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const {
    hasPermission: legacyHasPermissionToOverride,
    loading: legacyIsLoadingToOverride,
  } = useHasPermissionQuery('delete:change_request');

  const clearParentFromCache = useClearParentFromCache(parentId);

  const parentPath = useGetDetailParentPath(parentId ?? '', true);

  const { addNotification } = useNotifications();

  const [selectedChangeRequest, setSelectedChangeRequest] =
    useState<ChangeRequestPresentation | null>();

  const [previousSelectedChangeRequest, setPreviousSelectedChangeRequest] =
    useState<ChangeRequestPresentation | null>();

  // COI-777 — a CustomerSuccess actor is eligible for EVERY approver slot:
  // `perm_v2_is_eligible_approver` carries the bypass arm (migration
  // 1787560814000) and `recordApproverResponse` resolves CS onto the level's
  // unanswered slots, so the panel has to offer the control or the capability is
  // unreachable. Deliberately applied HERE rather than inside `isActiveApprover`
  // — that hook function also drives `RequiresAction` on the requests list
  // (useLabelledFields) and the `approvers` in-flight-edit rule, and a CS actor
  // is not "awaiting my response" on every pending CR in the org. Where it IS
  // used below, the surrounding conditions still scope it to the ACTIVE level of
  // a PENDING change request, exactly as for a real approver.
  const isCustomerSupport = useIsCustomerSupport();

  // V3 (trpc flag on): override / skip-level authority is the entity-type
  // Manager companion `${resource}_change_request:update` (RSP-1528 §11),
  // resolved for the selected CR's parent domain — the legacy
  // `delete:change_request` permission doesn't exist for V3 Managers, which
  // hid the override buttons from them. Flag off: legacy gate unchanged.
  const { canOverride, loading: canOverrideLoading } =
    useCanOverrideChangeRequest(
      trpcEnabled ? selectedChangeRequest?.parent?.ObjectType : undefined
    );
  const hasPermissionToOverride = trpcEnabled
    ? canOverride
    : legacyHasPermissionToOverride;
  const isLoadingToOverride = trpcEnabled
    ? canOverrideLoading
    : legacyIsLoadingToOverride;

  const [showChangeRequestOverrideModal, setShowChangeRequestOverrideModal] =
    useState(false);
  const [changeRequestOverrideMode, setChangeRequestOverrideMode] =
    useState<CHANGE_REQUEST_OVERRIDE_ACTION>(
      CHANGE_REQUEST_OVERRIDE_ACTION.OVERRIDE
    );

  useEffect(() => {
    if (changeRequests) {
      const selected = changeRequestId
        ? changeRequests.find((cr) => cr.Id === changeRequestId)
        : (changeRequests.filter(
            (cr) => cr.ChangeRequestStatus === Approval_Status_Enum.Pending
          )[0] ?? changeRequests[0]);
      setSelectedChangeRequest(selected);
    }
  }, [changeRequests, changeRequestId]);

  const changeRequestOptions = useMemo(() => {
    if (!changeRequests) {
      return [];
    }

    return changeRequests
      .filter((c) => c.SequentialId)
      .sort((a, b) => a.SequentialId! - b.SequentialId!)
      .map((cr) => ({
        label: `${getFriendlyId(
          Parent_Type_Enum.ChangeRequest,
          cr.SequentialId
        )} - ${toLocalDate(cr.CreatedAtTimestamp)} - ${
          statuses[cr.ChangeRequestStatus]
        }`,
        value: cr.Id,
      }));
  }, [changeRequests, statuses]);

  const handleResolved = useCallback(
    (changeRequest: ChangeRequestPresentation, status: ApprovalStatus) => {
      clearParentFromCache();
      if (
        changeRequest.Type === 'delete' &&
        status === Approval_Status_Enum.Approved
      ) {
        navigate(parentPath);
      }
      addNotification({
        type: status === 'approved' ? 'success' : 'error',
        content: `Request ${status}`,
      });
    },
    [clearParentFromCache, navigate, parentPath, addNotification]
  );

  useChangeRequestResolution(
    selectedChangeRequest,
    previousSelectedChangeRequest,
    handleResolved
  );

  const { submitApproverResponse, loading: updating } =
    useUpsertApproverResponses();

  const {
    isActiveApprover,
    activeLevelId,
    loading: isChangeRequestLoading,
  } = useChangeRequests(undefined);

  const { data: users, loading: usersLoading } = useGetUsers({ queryArgs: {} });

  const changeRequest = selectedChangeRequest;

  const activeLevel = useMemo(() => {
    if (changeRequest) {
      return activeLevelId(changeRequest);
    }

    return null;
  }, [changeRequest, activeLevelId]);

  const overriddenBy = useMemo(() => {
    if (changeRequest?.OverriddenByUser) {
      return users?.user.find(
        (user) => user.Id === changeRequest.OverriddenByUser
      );
    }

    return null;
  }, [changeRequest, users]);

  // The workflow the selected CR is gated by — drives the configured-levels
  // lookup (1-to-1 with the legacy panel). The level resolution itself is
  // server-side (getApprovalLevels resolves the per-entity AND global approval).
  const workflow =
    selectedChangeRequest?.responses[0]?.approver?.level?.approval?.Workflow;
  // Use the approval's own ParentId (from the response chain), not the panel
  // prop. Parent-scoped workflows (publish-document-version, acceptance,
  // issue-assessment) mount on the edited entity but the approval lives on the
  // parent — using the prop drops approvers into "No Level". No-op for
  // self-parented workflows where approval.ParentId === parentId.
  const approvalParentId = selectedChangeRequest?.responses.find(
    (response) => response.approver.level?.approval?.ParentId != null
  )?.approver.level?.approval?.ParentId;
  const { levels: configuredLevels, loading: levelsLoading } =
    useGetApprovalLevels(workflow ?? undefined, approvalParentId ?? parentId);

  const submitResponse = async (approved: boolean, comment: string) => {
    if (!selectedChangeRequest) {
      throw new Error('No change request selected');
    }

    if (!activeLevel) {
      throw new Error('No active level to approve');
    }

    // The actor's approver-slot resolution at this level happens server-side
    // (1-to-1 with the legacy Hasura action) — the panel only passes the level.
    await submitApproverResponse({
      changeRequestId: selectedChangeRequest.Id,
      parentId,
      levelId: activeLevel,
      approved,
      comment,
    });
  };

  if (
    !changeRequest ||
    crListLoading ||
    levelsLoading ||
    usersLoading ||
    isChangeRequestLoading ||
    isLoading ||
    isLoadingToOverride
  ) {
    return <Loading />;
  }

  // RSP-6087/6437: the displayed approval levels are EVERY level present on this
  // CR's responses, always ordered by `levelOrderKey` (specific-first, then
  // SequenceOrder — the same active-level-walk order). `configuredLevels` does
  // not affect ordering; it is consulted only to fill each level's
  // `ApprovalRuleType`. See `getDisplayLevels` for why a response level can be
  // missing from `configuredLevels` (mixed per-entity + global approval config —
  // the exact RSP-6437 scenario).
  const levels = getDisplayLevels(changeRequest.responses, configuredLevels);

  const responsesWithNoLevel = changeRequest.responses.filter(
    (response) =>
      !levels.map((lvl) => lvl.Id).includes(response.approver.level?.Id ?? '')
  );

  // COI-777 — see `isCustomerSupport` above: a CustomerSuccess actor is
  // eligible for every approver slot, so the panel must offer the control.
  const activeApprover =
    isActiveApprover(changeRequest, getOwners(selectedChangeRequest)) ||
    isCustomerSupport;

  return (
    <HelpPanel
      header={
        <div className={'w-48'}>
          <div className={'flex flex-col gap-6'}>
            <h2>
              {'Approval'}
              {changeRequest && (
                <span
                  className={'ml-3 text-grey font-normal'}
                  data-testid={'change-request-id'}
                >
                  {'('}
                  {getFriendlyId(
                    Parent_Type_Enum.ChangeRequest,
                    changeRequest.SequentialId
                  )}
                  {')'}
                </span>
              )}
            </h2>
            {changeRequestOptions.length > 1 && (
              <Select
                selectedOption={{
                  value: selectedChangeRequest.Id,
                  label: `${getFriendlyId(
                    Parent_Type_Enum.ChangeRequest,
                    selectedChangeRequest.SequentialId
                  )} - ${toLocalDate(
                    selectedChangeRequest.CreatedAtTimestamp
                  )}`,
                }}
                data-testid={'change-request-select'}
                options={changeRequestOptions}
                onChange={(evt) => {
                  setPreviousSelectedChangeRequest(selectedChangeRequest);
                  setSearchParams((prev) => {
                    prev.set(
                      'requestId',
                      evt.detail.selectedOption.value as string
                    );
                    prev.set('showRequest', 'true');

                    return prev;
                  });
                }}
              />
            )}
          </div>
          {hasPermissionToOverride &&
            changeRequest.ChangeRequestStatus ===
              Approval_Status_Enum.Pending && (
              <ButtonDropdown
                className={'absolute top-4 right-7'}
                expandToViewport
                ariaLabel={'Change request settings'}
                items={[
                  {
                    // COI-1054: SKIP targets a specific level, so it is
                    // genuinely impossible once the client-side walk has
                    // latched `activeLevel` to null (any recorded rejection
                    // does that). It used to stay enabled and silently do
                    // nothing, because the modal below was not rendered.
                    text: 'Skip workflow level',
                    id: CHANGE_REQUEST_OVERRIDE_ACTION.SKIP,
                    disabled: !activeLevel,
                    disabledReason: !activeLevel
                      ? 'No approval level is awaiting a response. Use Override workflow to close this request.'
                      : undefined,
                  },
                  {
                    text: 'Override workflow',
                    id: CHANGE_REQUEST_OVERRIDE_ACTION.OVERRIDE,
                    disabled: false,
                  },
                ]}
                variant={'icon'}
                onItemClick={(e) => {
                  if (
                    e.detail.id === CHANGE_REQUEST_OVERRIDE_ACTION.OVERRIDE ||
                    e.detail.id === CHANGE_REQUEST_OVERRIDE_ACTION.SKIP
                  ) {
                    setShowChangeRequestOverrideModal(true);
                    setChangeRequestOverrideMode(e.detail.id);
                  }
                }}
              />
            )}
        </div>
      }
    >
      <div className={'flex flex-col gap-6'}>
        {changeRequest.createdBy ? (
          <div>
            <h4>{'Requesters'}</h4>
            <div className={'flex flex-col gap-3'}>
              <UserPreview
                user={changeRequest.createdBy}
                responseDate={changeRequest.CreatedAtTimestamp}
                hideIcon={true}
              />
              {changeRequest.contributors.map((contributor, i) =>
                contributor.user ? (
                  <UserPreview
                    user={contributor.user}
                    key={i}
                    hideIcon={true}
                  />
                ) : null
              )}
            </div>
          </div>
        ) : null}
        {changeRequest.RequesterComment && (
          <Alert
            type={'info'}
            header={t('requester_rationale_header')}
            data-testid={'requester-rationale-alert'}
          >
            {changeRequest.RequesterComment}
          </Alert>
        )}
        {changeRequest.OverriddenAtTimestamp &&
          changeRequest.OverriddenByUser && (
            <div>
              <Alert type={'warning'} test-id={'override-alert'}>
                {'The change request was'}{' '}
                <b>{changeRequest.ChangeRequestStatus}</b>
                {' by'} {overriddenBy?.FriendlyName}
                {' on'} {toLocalDateTime(changeRequest.OverriddenAtTimestamp)}
                {'.'}
                <div className={'mt-6'}>
                  <b>{'Rationale: '}</b>
                  {changeRequest.Comment}
                </div>
              </Alert>
            </div>
          )}
        <div>
          <h4>{'Approval Steps'}</h4>
          {levels.map((level, i) => (
            <div
              key={level.Id}
              className={`mb-4 ${
                level.Id === activeLevel && !changeRequest.OverriddenAtTimestamp
                  ? 'opacity-100'
                  : 'opacity-50'
              }`}
            >
              <div
                className={`flex flex-col p-3 border-solid rounded-lg ${
                  level.Id === activeLevel
                    ? 'border animate-border-pulse'
                    : 'border border-grey150'
                }`}
              >
                <h5>
                  {'Level '}
                  {i + 1}
                </h5>
                <div className={'flex flex-col gap-3'}>
                  {changeRequest.responses
                    .filter(
                      (response) => response.approver.level?.Id === level.Id
                    )
                    .map((response, j) =>
                      response.approver.user || response.approver.group ? (
                        <div key={j}>
                          <UserPreview
                            user={response.approver.user}
                            group={response.approver.group}
                            response={
                              response.Approved === null &&
                              level.Id !== activeLevel
                                ? undefined
                                : response.Approved
                            }
                            showApproveReject={
                              response.Approved === null &&
                              activeApprover &&
                              (response.approver.OwnerApprover ||
                                isCustomerSupport ||
                                response.approver.user?.Id === user?.userId ||
                                response.approver.group?.users.some(
                                  (c) => c.UserId === user?.userId
                                )) &&
                              changeRequest.ChangeRequestStatus ===
                                Approval_Status_Enum.Pending &&
                              level.Id === activeLevel
                            }
                            submitResponse={submitResponse}
                            updating={updating}
                            comment={response.Comment}
                            hasPermissionToOverride={hasPermissionToOverride}
                            responseId={response.Id}
                            responseDate={
                              response.Approved
                                ? response.ModifiedAtTimestamp
                                : null
                            }
                            approvedByUserId={
                              response.Approved ? response.ApprovedByUser : null
                            }
                            currentUserId={user?.userId}
                          />
                        </div>
                      ) : (
                        <div key={j}>
                          <UserPreview
                            owner
                            response={
                              response.Approved === null &&
                              level.Id !== activeLevel
                                ? undefined
                                : response.Approved
                            }
                            showApproveReject={
                              response.Approved === null &&
                              activeApprover &&
                              changeRequest.ChangeRequestStatus ===
                                Approval_Status_Enum.Pending &&
                              level.Id === activeLevel
                            }
                            submitResponse={submitResponse}
                            updating={updating}
                            comment={response.Comment}
                            hasPermissionToOverride={hasPermissionToOverride}
                            responseId={response.Id}
                            responseDate={
                              response.Approved
                                ? response.ModifiedAtTimestamp
                                : null
                            }
                            approvedByUserId={
                              response.Approved ? response.ApprovedByUser : null
                            }
                          />
                        </div>
                      )
                    )}
                </div>
              </div>
            </div>
          ))}
          {responsesWithNoLevel.length > 0 && (
            <div className={'mb-4'}>
              <h5>{'No Level'}</h5>
              <div className={'flex flex-col gap-3'}>
                {responsesWithNoLevel.map((response, i) =>
                  response.approver.user ? (
                    <UserPreview
                      key={i}
                      user={response.approver.user}
                      response={
                        response.Approved === null
                          ? undefined
                          : response.Approved
                      }
                      responseId={response.Id}
                      responseDate={
                        response.Approved ? response.ModifiedAtTimestamp : null
                      }
                      approvedByUserId={
                        response.Approved ? response.ApprovedByUser : null
                      }
                    />
                  ) : (
                    <UserPreview
                      key={i}
                      owner
                      response={
                        response.Approved === null
                          ? undefined
                          : response.Approved
                      }
                      responseId={response.Id}
                      responseDate={
                        response.Approved ? response.ModifiedAtTimestamp : null
                      }
                      approvedByUserId={
                        response.Approved ? response.ApprovedByUser : null
                      }
                    />
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/*
        COI-1054: gating the whole modal on `activeLevel` made a pending
        change request unrecoverable. `computeActiveLevelId` latches to null
        on ANY recorded rejection, while the trigger above renders on
        permission + `Pending` alone — so the "Override workflow" button
        stayed enabled and rendered nothing. The OVERRIDE branch only ever
        sends `changeRequestId` / `comment` / `approved`; `activeLevel` is
        used solely by SKIP, which is now disabled when there is none.
      */}
      {(activeLevel ||
        changeRequestOverrideMode ===
          CHANGE_REQUEST_OVERRIDE_ACTION.OVERRIDE) && (
        <ChangeRequestOverrideModal
          changeRequestId={selectedChangeRequest.Id}
          onDismiss={() => {
            setShowChangeRequestOverrideModal(false);
          }}
          mode={changeRequestOverrideMode}
          visible={showChangeRequestOverrideModal}
          activeLevel={activeLevel}
        />
      )}
    </HelpPanel>
  );
};
